import React, { useState, useEffect } from 'react';
import "../../css/ExamSchedule.css";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaCalendarAlt, FaPlus, FaSearch, FaTimes, FaFileCsv, FaFilter, FaFileExport, FaChalkboardTeacher, FaEdit, FaTrash } from 'react-icons/fa';
import Loader from "../../components/Loader"


function ExamSchedule() {
    const [exams, setExams] = useState([]);
    const [filteredExams, setFilteredExams] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFilter, setSelectedFilter] = useState('all');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showResultModal, setShowResultModal] = useState(false);
    const [currentExam, setCurrentExam] = useState(null);
    const [fileUploaded, setFileUploaded] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [examToDelete, setExamToDelete] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [classes, setClasses] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [sections, setSections] = useState([]);
    const [selectedClass, setSelectedClass] = useState('');
    const [formData, setFormData] = useState({
        examName: '',
        subject: '',
        class: '',
        section: '',
        date: '',
        startTime: '',
        endTime: '',
        examType: 'regular',
        totalMarks: '100',
        passingMarks: '35',
        description: ''
    });
    const [editFormData, setEditFormData] = useState({
        examName: '',
        subject: '',
        class: '',
        section: '',
        date: '',
        startTime: '',
        endTime: '',
        examType: 'regular',
        totalMarks: '100',
        passingMarks: '35',
        description: ''
    });


    const fetchClasses = async () => {
        const token = localStorage.getItem("token");

        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        try {
            const response = await fetch("https://schoolpro-api.uniums.com/api/teacher/classes", {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch classes");
            }

            const data = await response.json();
            setClasses(data);
        } catch (error) {
            console.error("Error fetching classes:", error);
            toast.error(`Error: ${error.message}`);
        }
    };

    const fetchSubjects = async () => {
        const token = localStorage.getItem("token");

        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        try {
            const response = await fetch("https://schoolpro-api.uniums.com/api/teacher/subjects", {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch subjects");
            }

            const data = await response.json();
            setSubjects(data);
        } catch (error) {
            console.error("Error fetching subjects:", error);
            toast.error(`Error: ${error.message}`);
        }
    };

    // Function to handle class selection and set available sections
    const handleClassChange = (e) => {
        const classId = e.target.value;
        setSelectedClass(classId);

        // Find the selected class and get its sections
        const selectedClassObj = classes.find(c => c._id === classId);
        if (selectedClassObj && selectedClassObj.section) {
            // Split the comma-separated string into an array
            const sectionsArray = selectedClassObj.section.split(',').map(s => s.trim());
            // Add "All Sections" as an option
            setSections(['all', ...sectionsArray]);
        } else {
            setSections([]);
        }

        // Update form data
        setFormData({
            ...formData,
            class: classId,
            section: '' // Reset section when class changes
        });
    };

    // Call these functions when the component mounts
    useEffect(() => {
        fetchClasses();
        fetchSubjects();
    }, []);



    const fetchExams = async () => {
        setIsLoading(true);
        const token = localStorage.getItem("token");

        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            setIsLoading(false);
            return;
        }

        try {
            const response = await fetch("https://schoolpro-api.uniums.com/api/teacher/exams", {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch exams");
            }

            const data = await response.json();
            setExams(data);
            setFilteredExams(data);
        } catch (error) {
            console.error("Error fetching exams:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        fetchExams();
    }, []);





    useEffect(() => {
        let results = [...exams];

        // Apply search term filter
        if (searchTerm.trim() !== '') {
            results = results.filter(exam =>
                (exam.examName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (exam.subject?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (exam.class?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (exam.section?.toLowerCase() || '').includes(searchTerm.toLowerCase())
            );
        }

        // Apply selected filter
        if (selectedFilter === 'upcoming') {
            results = results.filter(exam => exam.status === 'upcoming');
        } else if (selectedFilter === 'completed') {
            results = results.filter(exam => exam.status === 'completed');
        } else if (selectedFilter === 'resultsUploaded') {
            results = results.filter(exam => exam.resultsUploaded);
        } else if (selectedFilter === 'resultsNotUploaded') {
            results = results.filter(exam => !exam.resultsUploaded);
        }

        setFilteredExams(results);
    }, [searchTerm, exams, selectedFilter]);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const clearSearch = () => {
        setSearchTerm('');
        setSelectedFilter('all');
    };

    const getStatusBadgeClass = (status) => {
        if (status === 'upcoming') return 'ExamSchedule-badge-upcoming';
        if (status === 'completed') return 'ExamSchedule-badge-completed';
        return '';
    };

    const getExamTypeClass = (examType) => {
        switch (examType) {
            case 'final': return 'ExamSchedule-card-final';
            case 'quarterly': return 'ExamSchedule-card-quarterly';
            case 'unit': return 'ExamSchedule-card-unit';
            default: return 'ExamSchedule-card-regular';
        }
    };

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleCreateExam = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem("token");

        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        setIsLoading(true);

        try {
            const selectedClassObj = classes.find(c => c._id === formData.class);
            const sectionToSubmit = formData.section === 'all' ? selectedClassObj.section : formData.section;
            const response = await fetch("https://schoolpro-api.uniums.com/api/teacher/exams/create", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    examName: formData.examName,
                    subject: formData.subject,
                    class: formData.class,
                    section: sectionToSubmit,
                    date: formData.date,
                    startTime: formData.startTime,
                    endTime: formData.endTime,
                    examType: formData.examType,
                    totalMarks: parseInt(formData.totalMarks),
                    passingMarks: parseInt(formData.passingMarks),
                    description: formData.description
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to create exam");
            }

            const data = await response.json();
            toast.success(data.message || "Exam created successfully!");

            // Refresh the exams list
            fetchExams();

            // Reset form and close modal
            setFormData({
                examName: '',
                subject: '',
                class: '',
                section: '',
                date: '',
                startTime: '',
                endTime: '',
                examType: 'regular',
                totalMarks: '100',
                passingMarks: '35',
                description: ''
            });
            setShowCreateModal(false);
        } catch (error) {
            console.error("Error creating exam:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenResultModal = (exam) => {
        const examData = {
            _id: exam._id || exam.id,
            examName: exam.examName,
            subject: exam.subject,
            class: exam.class,
            section: exam.section
        };
        setCurrentExam(examData);
        setShowResultModal(true);
        setFileUploaded(false);
    };
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            // In a real application, you would process the file here
            setTimeout(() => {
                setFileUploaded(true);
                toast.info("File uploaded and ready for processing");
            }, 1000);
        }
    };
    const handleSubmitResults = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem("token");

        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        if (!fileUploaded) {
            toast.error("Please upload a CSV file first.");
            return;
        }

        setIsLoading(true);

        try {
            const fileInput = document.getElementById('resultFile');
            const file = fileInput.files[0];

            if (!file) {
                throw new Error("No file selected");
            }

            // Convert file to base64
            const reader = new FileReader();
            reader.readAsDataURL(file);

            reader.onload = async () => {
                const csvData = reader.result;
                const examId = currentExam._id || currentExam.id;

                if (!examId) {
                    throw new Error("Exam ID is missing");
                }

                const response = await fetch(`https://schoolpro-api.uniums.com/api/teacher/exams/${examId}/upload-results`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    },
                    body: JSON.stringify({ csvData })
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || "Failed to upload results");
                }

                const data = await response.json();
                toast.success(data.message || "Exam results uploaded successfully!");

                // Refresh the exams list
                fetchExams();

                setShowResultModal(false);
                setCurrentExam(null);
                setFileUploaded(false);
                setIsLoading(false);
            };

            reader.onerror = (error) => {
                toast.error("Error reading file");
                setIsLoading(false);
            };

        } catch (error) {
            console.error("Error uploading results:", error);
            toast.error(`Error: ${error.message}`);
            setIsLoading(false);
        }
    };

    const fetchExamResults = async (examId) => {
        const token = localStorage.getItem("token");

        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return null;
        }

        try {
            const response = await fetch(`https://schoolpro-api.uniums.com/api/teacher/exams/${examId}/results`, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch exam results");
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching exam results:", error);
            toast.error(`Error: ${error.message}`);
            return null;
        }
    };

    const deleteExam = (examId) => {
        if (window.confirm("Are you sure you want to delete this exam?")) {
            const updatedExams = exams.filter(exam => exam.id !== examId);
            setExams(updatedExams);
            toast.success("Exam deleted successfully!");
        }
    };
    const handleDeleteClick = (exam) => {
        setExamToDelete(exam);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (examToDelete) {
            const token = localStorage.getItem("token");

            if (!token) {
                toast.error("Unauthorized! Please log in again.");
                return;
            }

            setIsLoading(true);

            try {
                // Check if we're using the correct ID property
                const examId = examToDelete._id || examToDelete.id;

                if (!examId) {
                    throw new Error("Exam ID is missing");
                }

                const response = await fetch(`https://schoolpro-api.uniums.com/api/teacher/exams/${examId}`, {
                    method: "DELETE",
                    headers: {
                        "Authorization": `Bearer ${token}`
                    }
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || "Failed to delete exam");
                }

                const data = await response.json();
                toast.success(data.message || "Exam deleted successfully!");

                // Refresh the exams list
                fetchExams();

                setShowDeleteModal(false);
                setExamToDelete(null);
            } catch (error) {
                console.error("Error deleting exam:", error);
                toast.error(`Error: ${error.message}`);
            } finally {
                setIsLoading(false);
            }
        }
    };
    const handleEditClick = (exam) => {
        setEditFormData({
            examName: exam.examName,
            subject: exam.subject,
            class: exam.class,
            section: exam.section,
            date: exam.date,
            startTime: exam.startTime,
            endTime: exam.endTime,
            examType: exam.examType,
            totalMarks: exam.totalMarks.toString(),
            passingMarks: exam.passingMarks.toString(),
            description: exam.description
        });
        setCurrentExam(exam);
        setShowEditModal(true);
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setEditFormData({
            ...editFormData,
            [name]: value
        });
    };

    const handleUpdateExam = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem("token");

        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        setIsLoading(true);

        try {
            const selectedClassObj = classes.find(c => c._id === editFormData.class);
            const sectionToSubmit = editFormData.section === 'all' ? selectedClassObj.section : editFormData.section;
            const examId = currentExam._id || currentExam.id;

            if (!examId) {
                throw new Error("Exam ID is missing");
            }

            const response = await fetch(`https://schoolpro-api.uniums.com/api/teacher/exams/${examId}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    examName: editFormData.examName,
                    subject: editFormData.subject,
                    class: editFormData.class,
                    section: sectionToSubmit,
                    date: editFormData.date,
                    startTime: editFormData.startTime,
                    endTime: editFormData.endTime,
                    examType: editFormData.examType,
                    totalMarks: parseInt(editFormData.totalMarks),
                    passingMarks: parseInt(editFormData.passingMarks),
                    description: editFormData.description
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to update exam");
            }

            const data = await response.json();
            toast.success(data.message || "Exam updated successfully!");

            // Refresh the exams list
            fetchExams();

            setShowEditModal(false);
            setCurrentExam(null);
        } catch (error) {
            console.error("Error updating exam:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const downloadTemplate = () => {
        // Create CSV content
        const headers = "Student Roll No,Student Name,Marks\n";
        const sampleData = "1001,John Doe,\n1002,Jane Smith,\n1003,Alex Johnson,\n";
        const csvContent = headers + sampleData;

        // Create a Blob with the CSV content
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

        // Create a URL for the Blob
        const url = URL.createObjectURL(blob);

        // Create a temporary link element
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${currentExam.subject}_${currentExam.class}${currentExam.section}_template.csv`);
        document.body.appendChild(link);

        // Trigger the download
        link.click();

        // Clean up
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // Add useEffect to populate sections when editing
    useEffect(() => {
        if (showEditModal && currentExam) {
            const editClass = classes.find(c => c._id === editFormData.class);
            if (editClass && editClass.sections) {
                setSections(editClass.sections);
            }
        }
    }, [showEditModal, currentExam, classes, editFormData.class]);

    // Handle class change in edit form
    const handleEditClassChange = (e) => {
        const classId = e.target.value;

        // Find the selected class and get its sections
        const selectedClassObj = classes.find(c => c._id === classId);
        if (selectedClassObj && selectedClassObj.section) {
            // Split the comma-separated string into an array
            const sectionsArray = selectedClassObj.section.split(',').map(s => s.trim());
            // Add "All Sections" as an option
            setSections(['all', ...sectionsArray]);
        } else {
            setSections([]);
        }

        // Update form data
        setEditFormData({
            ...editFormData,
            class: classId,
            section: '' // Reset section when class changes
        });
    };
    const getClassName = (classId) => {
        const classItem = classes.find(c => c._id === classId);
        return classItem ? classItem.name : 'Unknown Class';
    };

    const getSubjectName = (subjectId) => {
        const subject = subjects.find(s => s._id === subjectId);
        return subject ? subject.name : 'Unknown Subject';
    };
    const getSectionDisplay = (section) => {
        return section === 'all' ? 'All Sections' : section;
    };





    return (
        <div className="ExamSchedule-container">
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />

            <div className="ExamSchedule-header">
                <div className="ExamSchedule-title-container">
                    <FaCalendarAlt className="ExamSchedule-title-icon" />
                    <h1 className="ExamSchedule-title">Exam Schedule</h1>
                </div>

                <div className="ExamSchedule-stats">
                    <div className="ExamSchedule-stat-box">
                        <div className="ExamSchedule-stat-icon ExamSchedule-stat-icon-total">
                            <FaCalendarAlt />
                        </div>
                        <div className="ExamSchedule-stat-info">
                            <span className="ExamSchedule-stat-number">{exams.length}</span>
                            <span className="ExamSchedule-stat-label">Total Exams</span>
                        </div>
                    </div>
                    <div className="ExamSchedule-stat-box">
                        <div className="ExamSchedule-stat-icon ExamSchedule-stat-icon-upcoming">
                            <FaChalkboardTeacher />
                        </div>
                        <div className="ExamSchedule-stat-info">
                            <span className="ExamSchedule-stat-number">
                                {exams.filter(exam => exam.status === 'upcoming').length}
                            </span>
                            <span className="ExamSchedule-stat-label">Upcoming Exams</span>
                        </div>
                    </div>
                    <div className="ExamSchedule-stat-box">
                        <div className="ExamSchedule-stat-icon ExamSchedule-stat-icon-results">
                            <FaFileExport />
                        </div>
                        <div className="ExamSchedule-stat-info">
                            <span className="ExamSchedule-stat-number">
                                {exams.filter(exam => exam.resultsUploaded).length}
                            </span>
                            <span className="ExamSchedule-stat-label">Results Uploaded</span>
                        </div>
                    </div>
                </div>

                <button className="ExamSchedule-btn ExamSchedule-btn-create" onClick={() => setShowCreateModal(true)}>
                    <FaPlus /> Create New Exam
                </button>
            </div>

            <div className="ExamSchedule-filters">
                <div className="ExamSchedule-search-container">
                    <FaSearch className="ExamSchedule-search-icon" />
                    <input
                        type="text"
                        className="ExamSchedule-search-input"
                        placeholder="Search exams by name, subject, class or section..."
                        value={searchTerm}
                        onChange={handleSearch}
                    />
                    {searchTerm && (
                        <button className="ExamSchedule-search-clear" onClick={clearSearch}>
                            <FaTimes />
                        </button>
                    )}
                </div>

                <div className="ExamSchedule-filter-options">
                    <div className="ExamSchedule-filter-icon-container">
                        <FaFilter className="ExamSchedule-filter-icon" />
                    </div>
                    <select
                        className="ExamSchedule-filter-select"
                        value={selectedFilter}
                        onChange={(e) => setSelectedFilter(e.target.value)}
                    >
                        <option value="all">All Exams</option>
                        <option value="upcoming">Upcoming</option>
                        <option value="completed">Completed</option>
                        <option value="resultsUploaded">Results Uploaded</option>
                        <option value="resultsNotUploaded">Results Not Uploaded</option>
                    </select>
                </div>
            </div>

            <div className="ExamSchedule-list-container">
                {filteredExams.length === 0 ? (
                    <div className="ExamSchedule-empty-state">
                        <FaCalendarAlt className="ExamSchedule-empty-icon" />
                        <h3>No Exams Found</h3>
                        {searchTerm || selectedFilter !== 'all' ? (
                            <p>No exams match your current filters. Try adjusting your search criteria.</p>
                        ) : (
                            <p>There are no exams scheduled in the system.</p>
                        )}
                        {(searchTerm || selectedFilter !== 'all') && (
                            <button className="ExamSchedule-btn ExamSchedule-btn-primary" onClick={clearSearch}>
                                Clear Filters
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="ExamSchedule-grid">
                        {filteredExams.map((exam) => {
                            const examTypeClass = getExamTypeClass(exam.examType);
                            const statusBadgeClass = getStatusBadgeClass(exam.status);

                            return (
                                <div
                                    key={exam.id}
                                    className={`ExamSchedule-card ${examTypeClass}`}
                                >
                                    <div className="ExamSchedule-card-header">
                                        <div className={`ExamSchedule-card-badge ${statusBadgeClass}`}>
                                            {exam.status === 'upcoming' ? 'Upcoming' : 'Completed'}
                                        </div>
                                        <div className={`ExamSchedule-card-type-badge`}>
                                            {exam.examType === 'regular' ? 'Regular' :
                                                exam.examType === 'final' ? 'Final' :
                                                    exam.examType === 'quarterly' ? 'Quarterly' : 'Unit Test'}
                                        </div>
                                        <h3 className="ExamSchedule-card-title">
                                            {exam.examName}
                                        </h3>
                                    </div>
                                    <div className="ExamSchedule-card-body">
                                        <div className="ExamSchedule-card-detail-row">
                                            <div className="ExamSchedule-card-detail">
                                                <span className="ExamSchedule-card-label">Subject:</span>
                                                <span className="ExamSchedule-card-value">{getSubjectName(exam.subject)}</span>
                                            </div>
                                            <div className="ExamSchedule-card-detail">
                                                <span className="ExamSchedule-card-label">Class:</span>
                                                <span className="ExamSchedule-card-value">{getClassName(exam.class)} - {getSectionDisplay(exam.section)}</span>
                                            </div>
                                        </div>
                                        <div className="ExamSchedule-card-detail-row">
                                            <div className="ExamSchedule-card-detail">
                                                <span className="ExamSchedule-card-label">Date:</span>
                                                <span className="ExamSchedule-card-value">{formatDate(exam.date)}</span>
                                            </div>
                                            <div className="ExamSchedule-card-detail">
                                                <span className="ExamSchedule-card-label">Time:</span>
                                                <span className="ExamSchedule-card-value">{exam.startTime} - {exam.endTime}</span>
                                            </div>
                                        </div>
                                        <div className="ExamSchedule-card-detail-row">
                                            <div className="ExamSchedule-card-detail">
                                                <span className="ExamSchedule-card-label">Total Marks:</span>
                                                <span className="ExamSchedule-card-value">{exam.totalMarks}</span>
                                            </div>
                                            <div className="ExamSchedule-card-detail">
                                                <span className="ExamSchedule-card-label">Passing Marks:</span>
                                                <span className="ExamSchedule-card-value">{exam.passingMarks}</span>
                                            </div>
                                        </div>
                                        <div className="ExamSchedule-card-description">
                                            <span className="ExamSchedule-card-label">Description:</span>
                                            <p className="ExamSchedule-card-description-text">{exam.description}</p>
                                        </div>
                                    </div>
                                    <div className="ExamSchedule-card-footer">
                                        <div className="ExamSchedule-card-results-status">
                                            <span className={`ExamSchedule-card-results-badge ${exam.resultsUploaded ? 'ExamSchedule-results-uploaded' : 'ExamSchedule-results-pending'}`}>
                                                {exam.resultsUploaded ? 'Results Uploaded' : 'Results Pending'}
                                            </span>
                                        </div>
                                        <div className="ExamSchedule-card-actions">
                                            <button className="ExamSchedule-card-btn ExamSchedule-card-btn-edit" onClick={() => handleEditClick(exam)}>
                                                <FaEdit />
                                            </button>
                                            <button
                                                className="ExamSchedule-card-btn ExamSchedule-card-btn-delete"
                                                onClick={() => handleDeleteClick(exam)}
                                            >
                                                <FaTrash />
                                            </button>
                                            {!exam.resultsUploaded && (
                                                <button
                                                    className="ExamSchedule-card-btn ExamSchedule-card-btn-upload"
                                                    onClick={() => handleOpenResultModal(exam)}
                                                >
                                                    Upload Results
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Create Exam Modal */}
            {showCreateModal && (
                <div className="ExamSchedule-modal-overlay">
                    <div className="ExamSchedule-modal">
                        <div className="ExamSchedule-modal-header">
                            <h2 className="ExamSchedule-modal-title">Create New Exam</h2>
                            <button className="ExamSchedule-modal-close" onClick={() => setShowCreateModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <form onSubmit={handleCreateExam} className="ExamSchedule-form">
                            <div className="ExamSchedule-form-row">
                                <div className="ExamSchedule-form-group">
                                    <label className="ExamSchedule-form-label">Exam Name</label>
                                    <input
                                        type="text"
                                        name="examName"
                                        className="ExamSchedule-form-input"
                                        placeholder="Mid-Term Examination"
                                        value={formData.examName}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="ExamSchedule-form-group">
                                    <label className="ExamSchedule-form-label">Subject</label>
                                    <select
                                        name="subject"
                                        className="ExamSchedule-form-select"
                                        value={formData.subject}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Select Subject</option>
                                        {subjects.map(subject => (
                                            <option key={subject._id} value={subject._id}>
                                                {subject.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="ExamSchedule-form-row">
                                <div className="ExamSchedule-form-group">
                                    <label className="ExamSchedule-form-label">Class</label>
                                    <select
                                        name="class"
                                        className="ExamSchedule-form-select"
                                        value={formData.class}
                                        onChange={handleClassChange}
                                        required
                                    >
                                        <option value="">Select Class</option>
                                        {classes.map(classItem => (
                                            <option key={classItem._id} value={classItem._id}>
                                                {classItem.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="ExamSchedule-form-group">
                                    <label className="ExamSchedule-form-label">Section</label>
                                    <select
                                        name="section"
                                        className="ExamSchedule-form-select"
                                        value={formData.section}
                                        onChange={handleInputChange}
                                        required
                                        disabled={!selectedClass}
                                    >
                                        <option value="">Select Section</option>
                                        {sections.map(section => (
                                            <option key={section} value={section}>
                                                {section === 'all' ? 'All Sections' : section}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="ExamSchedule-form-row">
                                <div className="ExamSchedule-form-group">
                                    <label className="ExamSchedule-form-label">Date</label>
                                    <input
                                        type="date"
                                        name="date"
                                        className="ExamSchedule-form-input"
                                        value={formData.date}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="ExamSchedule-form-group">
                                    <label className="ExamSchedule-form-label">Exam Type</label>
                                    <select
                                        name="examType"
                                        className="ExamSchedule-form-select"
                                        value={formData.examType}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="regular">Regular</option>
                                        <option value="unit">Unit Test</option>
                                        <option value="quarterly">Quarterly</option>
                                        <option value="final">Final</option>
                                    </select>
                                </div>
                            </div>

                            <div className="ExamSchedule-form-row">
                                <div className="ExamSchedule-form-group">
                                    <label className="ExamSchedule-form-label">Start Time</label>
                                    <input
                                        type="time"
                                        name="startTime"
                                        className="ExamSchedule-form-input"
                                        value={formData.startTime}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="ExamSchedule-form-group">
                                    <label className="ExamSchedule-form-label">End Time</label>
                                    <input
                                        type="time"
                                        name="endTime"
                                        className="ExamSchedule-form-input"
                                        value={formData.endTime}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="ExamSchedule-form-row">
                                <div className="ExamSchedule-form-group">
                                    <label className="ExamSchedule-form-label">Total Marks</label>
                                    <input
                                        type="number"
                                        name="totalMarks"
                                        className="ExamSchedule-form-input"
                                        placeholder="100"
                                        value={formData.totalMarks}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="ExamSchedule-form-group">
                                    <label className="ExamSchedule-form-label">Passing Marks</label>
                                    <input
                                        type="number"
                                        name="passingMarks"
                                        className="ExamSchedule-form-input"
                                        placeholder="35"
                                        value={formData.passingMarks}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="ExamSchedule-form-group">
                                <label className="ExamSchedule-form-label">Description</label>
                                <textarea
                                    name="description"
                                    className="ExamSchedule-form-textarea"
                                    placeholder="Enter exam description, syllabus or special instructions..."
                                    value={formData.description}
                                    onChange={handleInputChange}
                                ></textarea>
                            </div>

                            <div className="ExamSchedule-form-actions">
                                <button type="button" className="ExamSchedule-btn ExamSchedule-btn-secondary" onClick={() => setShowCreateModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="ExamSchedule-btn ExamSchedule-btn-primary">
                                    Create Exam
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Upload Results Modal */}
            {showResultModal && currentExam && (
                <div className="ExamSchedule-modal-overlay">
                    <div className="ExamSchedule-modal ExamSchedule-results-modal">
                        <div className="ExamSchedule-modal-header">
                            <h2 className="ExamSchedule-modal-title">Upload Exam Results</h2>
                            <button className="ExamSchedule-modal-close" onClick={() => setShowResultModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <form onSubmit={handleSubmitResults} className="ExamSchedule-form">
                            <div className="ExamSchedule-result-info">
                                <div className="ExamSchedule-result-info-item">
                                    <span className="ExamSchedule-result-info-label">Exam:</span>
                                    <span className="ExamSchedule-result-info-value">{currentExam.examName}</span>
                                </div>
                                <div className="ExamSchedule-result-info-item">
                                    <span className="ExamSchedule-result-info-label">Subject:</span>
                                    <span className="ExamSchedule-result-info-value">{currentExam.subject}</span>
                                </div>
                                <div className="ExamSchedule-result-info-item">
                                    <span className="ExamSchedule-result-info-label">Class & Section:</span>
                                    <span className="ExamSchedule-result-info-value">{currentExam.class} - {currentExam.section}</span>
                                </div>
                            </div>

                            <div className="ExamSchedule-upload-section">
                                <div className="ExamSchedule-upload-instructions">
                                    <h4>Upload Instructions</h4>
                                    <ul>
                                        <li>Upload results in CSV format</li>
                                        <li>File must include student ID, student name, and marks columns</li>
                                        <li>Maximum file size: 5MB</li>
                                    </ul>
                                    <div className="ExamSchedule-template-download">
                                        <a href="#" className="ExamSchedule-link" onClick={(e) => {
                                            e.preventDefault();
                                            downloadTemplate();
                                        }}>
                                            <FaFileCsv /> Download template
                                        </a>
                                    </div>
                                </div>

                                <div className="ExamSchedule-file-drop">
                                    <input
                                        type="file"
                                        id="resultFile"
                                        className="ExamSchedule-file-input"
                                        accept=".csv,.xlsx,.xls"
                                        onChange={handleFileUpload}
                                    />
                                    <label htmlFor="resultFile" className="ExamSchedule-file-label">
                                        {fileUploaded ? (
                                            <div className="ExamSchedule-file-success">
                                                <FaFileCsv className="ExamSchedule-file-icon" />
                                                <span>results_data.csv uploaded successfully</span>
                                            </div>
                                        ) : (
                                            <>
                                                <FaFileCsv className="ExamSchedule-file-icon" />
                                                <span>Drag & drop your file here or click to browse</span>
                                                <span className="ExamSchedule-file-hint">.csv, .xlsx or .xls</span>
                                            </>
                                        )}
                                    </label>
                                </div>
                            </div>

                            <div className="ExamSchedule-form-actions">
                                <button type="button" className="ExamSchedule-btn ExamSchedule-btn-secondary" onClick={() => setShowResultModal(false)}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="ExamSchedule-btn ExamSchedule-btn-primary"
                                    disabled={!fileUploaded}
                                >
                                    Upload Results
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {/* Delete Confirmation Modal */}
            {showDeleteModal && examToDelete && (
                <div className="ExamSchedule-modal-overlay">
                    <div className="ExamSchedule-modal ExamSchedule-delete-modal">
                        <div className="ExamSchedule-modal-header">
                            <h2 className="ExamSchedule-modal-title">Delete Exam</h2>
                            <button className="ExamSchedule-modal-close" onClick={() => setShowDeleteModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="ExamSchedule-form">
                            <div className="ExamSchedule-delete-warning">
                                <p>Are you sure you want to delete "{examToDelete.examName}"?</p>
                                <p>This action cannot be undone.</p>
                            </div>
                            <div className="ExamSchedule-form-actions">
                                <button
                                    type="button"
                                    className="ExamSchedule-btn ExamSchedule-btn-secondary"
                                    onClick={() => setShowDeleteModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="ExamSchedule-btn ExamSchedule-btn-danger"
                                    onClick={confirmDelete}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Exam Modal */}
            {showEditModal && currentExam && (
                <div className="ExamSchedule-modal-overlay">
                    <div className="ExamSchedule-modal">
                        <div className="ExamSchedule-modal-header">
                            <h2 className="ExamSchedule-modal-title">Edit Exam</h2>
                            <button className="ExamSchedule-modal-close" onClick={() => setShowEditModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <form onSubmit={handleUpdateExam} className="ExamSchedule-form">
                            <div className="ExamSchedule-form-row">
                                <div className="ExamSchedule-form-group">
                                    <label className="ExamSchedule-form-label">Exam Name</label>
                                    <input
                                        type="text"
                                        name="examName"
                                        className="ExamSchedule-form-input"
                                        placeholder="Mid-Term Examination"
                                        value={editFormData.examName}
                                        onChange={handleEditInputChange}
                                        required
                                    />
                                </div>
                                <div className="ExamSchedule-form-group">
                                    <label className="ExamSchedule-form-label">Subject</label>
                                    <select
                                        name="subject"
                                        className="ExamSchedule-form-select"
                                        value={editFormData.subject}
                                        onChange={handleEditInputChange}
                                        required
                                    >
                                        <option value="">Select Subject</option>
                                        {subjects.map(subject => (
                                            <option key={subject._id} value={subject._id}>
                                                {subject.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="ExamSchedule-form-row">
                                <div className="ExamSchedule-form-group">
                                    <label className="ExamSchedule-form-label">Class</label>
                                    <select
                                        name="class"
                                        className="ExamSchedule-form-select"
                                        value={editFormData.class}
                                        onChange={handleEditClassChange}
                                        required
                                    >
                                        <option value="">Select Class</option>
                                        {classes.map(classItem => (
                                            <option key={classItem._id} value={classItem._id}>
                                                {classItem.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="ExamSchedule-form-group">
                                    <label className="ExamSchedule-form-label">Section</label>
                                    <select
                                        name="section"
                                        className="ExamSchedule-form-select"
                                        value={editFormData.section}
                                        onChange={handleEditInputChange}
                                        required
                                    >
                                        <option value="">Select Section</option>
                                        {sections.map(section => (
                                            <option key={section} value={section}>
                                                {section === 'all' ? 'All Sections' : section}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="ExamSchedule-form-row">
                                <div className="ExamSchedule-form-group">
                                    <label className="ExamSchedule-form-label">Date</label>
                                    <input
                                        type="date"
                                        name="date"
                                        className="ExamSchedule-form-input"
                                        value={editFormData.date}
                                        onChange={handleEditInputChange}
                                        required
                                    />
                                </div>
                                <div className="ExamSchedule-form-group">
                                    <label className="ExamSchedule-form-label">Exam Type</label>
                                    <select
                                        name="examType"
                                        className="ExamSchedule-form-select"
                                        value={editFormData.examType}
                                        onChange={handleEditInputChange}
                                        required
                                    >
                                        <option value="regular">Regular</option>
                                        <option value="unit">Unit Test</option>
                                        <option value="quarterly">Quarterly</option>
                                        <option value="final">Final</option>
                                    </select>
                                </div>
                            </div>

                            <div className="ExamSchedule-form-row">
                                <div className="ExamSchedule-form-group">
                                    <label className="ExamSchedule-form-label">Start Time</label>
                                    <input
                                        type="time"
                                        name="startTime"
                                        className="ExamSchedule-form-input"
                                        value={editFormData.startTime}
                                        onChange={handleEditInputChange}
                                        required
                                    />
                                </div>
                                <div className="ExamSchedule-form-group">
                                    <label className="ExamSchedule-form-label">End Time</label>
                                    <input
                                        type="time"
                                        name="endTime"
                                        className="ExamSchedule-form-input"
                                        value={editFormData.endTime}
                                        onChange={handleEditInputChange}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="ExamSchedule-form-row">
                                <div className="ExamSchedule-form-group">
                                    <label className="ExamSchedule-form-label">Total Marks</label>
                                    <input
                                        type="number"
                                        name="totalMarks"
                                        className="ExamSchedule-form-input"
                                        placeholder="100"
                                        value={editFormData.totalMarks}
                                        onChange={handleEditInputChange}
                                        required
                                    />
                                </div>
                                <div className="ExamSchedule-form-group">
                                    <label className="ExamSchedule-form-label">Passing Marks</label>
                                    <input
                                        type="number"
                                        name="passingMarks"
                                        className="ExamSchedule-form-input"
                                        placeholder="35"
                                        value={editFormData.passingMarks}
                                        onChange={handleEditInputChange}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="ExamSchedule-form-group">
                                <label className="ExamSchedule-form-label">Description</label>
                                <textarea
                                    name="description"
                                    className="ExamSchedule-form-textarea"
                                    placeholder="Enter exam description, syllabus or special instructions..."
                                    value={editFormData.description}
                                    onChange={handleEditInputChange}
                                ></textarea>
                            </div>

                            <div className="ExamSchedule-form-actions">
                                <button type="button" className="ExamSchedule-btn ExamSchedule-btn-secondary" onClick={() => setShowEditModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="ExamSchedule-btn ExamSchedule-btn-primary">
                                    Update Exam
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {isLoading && (
                <>
                    <Loader />
                    <p>Loading...</p>
                </>

            )}

        </div>
    );
}

export default ExamSchedule;